import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleStop, Mic } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  apiPutStoryQuestionById,
  APIPutStoryQuestionPayload,
  StoryQuestion,
} from '@/shared/api';
import { storyByIdQueryOptions } from '@/shared/queries';
import { Button } from '@/shared/ui/button';

type StoryQuestionItemAudioRecorderProps = {
  audioAnswer: string | null;
  storyId: string;
  id: string;
  guest?: boolean;
};

export const StoryQuestionItemAudioRecorder = ({
  audioAnswer,
  storyId,
  id,
  guest,
}: StoryQuestionItemAudioRecorderProps) => {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const queryClient = useQueryClient();
  const intl = useIntl();

  const updateStoryQuestionMutation = useMutation<
    StoryQuestion,
    Error,
    APIPutStoryQuestionPayload
  >({
    mutationFn: (updatedStoryQuestion) => {
      return apiPutStoryQuestionById(String(id), updatedStoryQuestion).then(
        ({ data }) => data,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: storyByIdQueryOptions(storyId).queryKey,
      });
    },
  });

  useEffect(() => {
    if (!audioAnswer) {
      return;
    }

    const uint8Array = base64ToUint8Array(audioAnswer);

    const blob = new Blob([uint8Array], { type: 'audio/mpeg' });
    setAudioURL(window.URL.createObjectURL(blob));
  }, [audioAnswer]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Reset audio chunks
      audioChunksRef.current = [];

      // Create new media recorder instance
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      // Handle data available event
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Handle recording stop event
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioURL(audioUrl);
        stream.getTracks().forEach((track) => track.stop());

        // Convert blob to base64
        const reader = new FileReader();

        reader.onloadend = async () => {
          const base64data = (reader.result as string).split(',')[1];

          updateStoryQuestionMutation.mutate({ audioAnswer: base64data });
        };

        // Start reading the blob as data URL
        reader.readAsDataURL(audioBlob);
      };

      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <>
      {guest && (
        <>
          <p className="text-sm text-gray-400">
            {intl.formatMessage({ id: 'storyQuestion.audioAnswer.or' })}
          </p>

          <div>
            {isRecording ? (
              <Button variant="outline" onClick={stopRecording}>
                {intl.formatMessage({ id: 'storyQuestion.audioAnswer.stop' })}
                <CircleStop />
              </Button>
            ) : (
              <Button variant="outline" onClick={startRecording}>
                {intl.formatMessage({ id: 'storyQuestion.audioAnswer.start' })}
                <Mic />
              </Button>
            )}
          </div>
        </>
      )}

      {audioURL && (
        <>
          <p>{intl.formatMessage({ id: 'storyQuestion.audioAnswer' })}</p>
          <audio src={audioURL} controls preload="auto" />
        </>
      )}
    </>
  );
};

function base64ToUint8Array(base64: string) {
  const binaryString = atob(base64); // atob decodes Base64 into a binary string
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}
